import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowUpRight, ClipboardList, Search, Users, Microscope, Handshake, ShieldCheck, HelpCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/Button';
const stages = [{
  num: '01',
  icon: ClipboardList,
  title: 'Scoping & Position Brief',
  duration: 'Week 1',
  desc: 'We begin with an intensive scoping engagement — confidential interviews with the board, CEO, hiring leaders, and key stakeholders. We develop a detailed position specification, define success criteria, and align on the cultural and strategic dimensions that define the right hire.',
  deliverables: ['Stakeholder interviews & alignment', 'Position specification document', 'Search strategy & target universe', 'Compensation benchmarking']
}, {
  num: '02',
  icon: Search,
  title: 'Research & Market Mapping',
  duration: 'Weeks 2–4',
  desc: 'Our research team executes a systematic mapping of the talent universe — across geographies, sectors, and adjacent industries. We identify and qualify a long list of prospects, drawing on our proprietary database, sector relationships, and primary research.',
  deliverables: ['Comprehensive market map', 'Long list of qualified candidates', 'Sector intelligence briefing', 'Diversity & inclusion considerations']
}, {
  num: '03',
  icon: Users,
  title: 'Outreach & Engagement',
  duration: 'Weeks 3–6',
  desc: 'We engage prospects confidentially, always representing the opportunity with the gravity it deserves. Initial conversations focus on mutual fit, motivation, and qualification. Throughout, we maintain absolute discretion — for your organization and for the candidates we approach.',
  deliverables: ['Confidential candidate outreach', 'Initial qualification interviews', 'Weekly progress reporting', 'Shortlist development']
}, {
  num: '04',
  icon: Microscope,
  title: 'Assessment & Due Diligence',
  duration: 'Weeks 5–8',
  desc: 'Shortlisted candidates undergo rigorous assessment — including structured behavioral interviews, competency-based evaluation, leadership style analysis, and (where appropriate) psychometric assessment. We surface not just capability, but motivation, judgment, and fit.',
  deliverables: ['Deep-dive consultant interviews', 'Competency & leadership assessment', 'Candidate dossiers with recommendations', 'Reference & background due diligence']
}, {
  num: '05',
  icon: Handshake,
  title: 'Offer & Negotiation',
  duration: 'Weeks 8–10',
  desc: 'We support both sides through the offer process — advising on structure, helping navigate competing offers, and serving as a trusted intermediary. We have seen every conceivable negotiation dynamic and bring that experience to bear on your behalf.',
  deliverables: ['Compensation structuring advisory', 'Offer presentation & negotiation', 'Counter-offer management', 'Final reference verification']
}, {
  num: '06',
  icon: ShieldCheck,
  title: 'Onboarding & Integration',
  duration: 'Months 3–12',
  desc: 'Our engagement does not end at signing. We provide structured onboarding support, executive integration coaching, and quarterly check-ins through the first year — protecting your investment and giving the new leader every advantage in their first 12 months.',
  deliverables: ['First 100-day integration plan', 'Executive onboarding coaching', 'Quarterly stakeholder check-ins', '12-month placement guarantee']
}];
const principles = [{
  title: 'Senior-led, never delegated',
  desc: 'Every mandate is led personally by a senior consultant from start to finish. No junior researchers running searches behind the scenes.'
}, {
  title: 'Shortlists, not longlists',
  desc: 'We present a curated shortlist of three to four candidates we genuinely recommend — not volume to give the illusion of optionality.'
}, {
  title: 'Discretion as a default',
  desc: 'Every search is conducted under strict confidentiality protocols. We have handled the most sensitive leadership transitions across global markets.'
}, {
  title: 'Measured in retention',
  desc: 'We are evaluated by our two-year retention rate (currently 94%), not by speed of placement. That alignment changes everything.'
}];
export function HowItWorksPage() {
  return <div className="bg-white" data-id="element-635">
      {/* ─── HERO ─────────────────────────────────────────────── */}
      <section className="relative bg-navy-900 text-white pt-32 pb-24 overflow-hidden" data-id="element-636">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gold-500/5 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3" data-id="element-637" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12" data-id="element-638">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end" data-id="element-639">
            <div className="lg:col-span-8" data-id="element-640">
              <div className="flex items-center gap-3 mb-8" data-id="element-641">
                <div className="w-12 h-px bg-gold-500" data-id="element-642" />
                <span className="text-gold-500 font-semibold tracking-[0.3em] uppercase text-xs" data-id="element-643">
                  Our Search Process
                </span>
              </div>
              <motion.h1 initial={{
              opacity: 0,
              y: 30
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.8
            }} className="text-4xl md:text-6xl lg:text-7xl font-serif font-bold leading-[1.05] mb-8 tracking-tight" data-id="element-644">
                A disciplined methodology for the{' '}
                <span className="italic text-gold-500" data-id="element-645">most consequential</span>{' '}
                searches.
              </motion.h1>
            </div>
            <div className="lg:col-span-4" data-id="element-646">
              <p className="text-xl text-gray-300 leading-relaxed font-light" data-id="element-647">
                Six rigorous stages. One commitment: to deliver a leader who
                transforms your organization — not just fills the role.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── METHODOLOGY TIMELINE ─────────────────────────────── */}
      <section className="py-28 bg-white" data-id="element-648">
        <div className="max-w-7xl mx-auto px-6 lg:px-12" data-id="element-649">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-20" data-id="element-650">
            <div className="lg:col-span-5" data-id="element-651">
              <div className="flex items-center gap-3 mb-6" data-id="element-652">
                <div className="w-12 h-px bg-gold-500" data-id="element-653" />
                <span className="text-gold-600 font-semibold tracking-[0.25em] uppercase text-xs" data-id="element-654">
                  The Methodology
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-serif font-bold text-navy-900 leading-tight" data-id="element-655">
                Six stages. Twelve weeks. One transformative hire.
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 flex items-end" data-id="element-656">
              <p className="text-lg text-gray-600 leading-relaxed" data-id="element-657">
                Our process has been refined over 25 years of executive search
                engagements. It is rigorous by design — because consequential
                leadership decisions deserve nothing less.
              </p>
            </div>
          </div>

          {/* Stages */}
          <div className="space-y-px bg-gray-200" data-id="element-658">
            {stages.map((stage, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.05
          }} className="bg-white p-10 md:p-14 group hover:bg-gray-50 transition-colors duration-500" data-id="element-659">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10" data-id="element-660">
                  {/* Number + Duration */}
                  <div className="lg:col-span-3" data-id="element-661">
                    <div className="flex items-center gap-4 mb-6" data-id="element-662">
                      <span className="text-6xl font-serif text-gold-500 leading-none" data-id="element-663">
                        {stage.num}
                      </span>
                    </div>
                    <div className="text-xs uppercase tracking-[0.25em] text-gray-400 font-semibold mb-2" data-id="element-664">
                      Duration
                    </div>
                    <div className="text-navy-900 font-bold text-lg mb-6" data-id="element-665">
                      {stage.duration}
                    </div>
                    <stage.icon className="h-8 w-8 text-navy-900" data-id="element-666" />
                  </div>

                  {/* Body */}
                  <div className="lg:col-span-6" data-id="element-667">
                    <h3 className="text-3xl font-serif font-bold text-navy-900 leading-tight mb-5" data-id="element-668">
                      {stage.title}
                    </h3>
                    <p className="text-lg text-gray-600 leading-relaxed" data-id="element-669">
                      {stage.desc}
                    </p>
                  </div>

                  {/* Deliverables */}
                  <div className="lg:col-span-3 lg:border-l lg:border-gray-200 lg:pl-8" data-id="element-670">
                    <div className="text-xs uppercase tracking-widest text-gold-600 font-semibold mb-4" data-id="element-671">
                      Deliverables
                    </div>
                    <ul className="space-y-3" data-id="element-672">
                      {stage.deliverables.map((d, i) => <li key={i} className="flex items-start gap-2 text-sm text-gray-700" data-id="element-673">
                          <CheckCircle2 className="h-4 w-4 text-gold-500 mt-0.5 flex-shrink-0" data-id="element-674" />
                          <span data-id="element-675">{d}</span>
                        </li>)}
                    </ul>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* ─── GUIDING PRINCIPLES ───────────────────────────────── */}
      <section className="py-28 bg-navy-900 text-white relative overflow-hidden" data-id="element-676">
        <div className="absolute top-0 left-0 w-96 h-96 bg-gold-500/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" data-id="element-677" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12" data-id="element-678">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16" data-id="element-679">
            <div className="lg:col-span-5" data-id="element-680">
              <div className="flex items-center gap-3 mb-6" data-id="element-681">
                <div className="w-12 h-px bg-gold-500" data-id="element-682" />
                <span className="text-gold-500 font-semibold tracking-[0.25em] uppercase text-xs" data-id="element-683">
                  Guiding Principles
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-serif font-bold leading-tight mb-6" data-id="element-684">
                What separates our process from the rest.
              </h2>
              <p className="text-lg text-gray-300 leading-relaxed" data-id="element-685">
                Methodology matters. But so do the principles that shape how we
                execute it.
              </p>
            </div>

            <div className="lg:col-span-6 lg:col-start-7 grid grid-cols-1 gap-1" data-id="element-686">
              {principles.map((p, i) => <motion.div key={i} initial={{
              opacity: 0,
              x: 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.1
            }} className="py-6 border-b border-white/10 last:border-b-0" data-id="element-687">
                  <h3 className="text-xl font-serif font-bold mb-2 text-white" data-id="element-688">
                    {p.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-689">{p.desc}</p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>

      {/* ─── FAQ ──────────────────────────────────────────────── */}
      <section className="py-28 bg-gray-50" data-id="element-690">
        <div className="max-w-5xl mx-auto px-6 lg:px-12" data-id="element-691">
          <div className="text-center mb-16 max-w-3xl mx-auto" data-id="element-692">
            <div className="flex items-center justify-center gap-3 mb-6" data-id="element-693">
              <div className="w-12 h-px bg-gold-500" data-id="element-694" />
              <span className="text-gold-600 font-semibold tracking-[0.25em] uppercase text-xs" data-id="element-695">
                Common Questions
              </span>
              <div className="w-12 h-px bg-gold-500" data-id="element-696" />
            </div>
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-navy-900 leading-tight" data-id="element-697">
              What clients ask before engaging us.
            </h2>
          </div>

          <div className="space-y-px bg-gray-200" data-id="element-698">
            {[{
            q: 'How long does a typical executive search take?',
            a: 'Most retained C-suite and board searches run 12–16 weeks from kickoff to signed offer. We can move faster when the situation demands, but we do not compromise rigor for speed.'
          }, {
            q: 'How do you ensure confidentiality?',
            a: 'Every engagement is governed by formal confidentiality protocols. Candidate approaches are made under our firm’s name, not yours. Internal stakeholders are read in on a strict need-to-know basis. We have managed CEO transitions where the incumbent was unaware until the day of announcement.'
          }, {
            q: 'What is your placement guarantee?',
            a: 'For all retained executive search engagements, we offer a 12-month guarantee: if the placed executive departs (other than for cause initiated by your organization) within the first year, we will replace them at no additional retainer fee.'
          }, {
            q: 'How are fees structured?',
            a: 'Retained search fees are typically 33% of the placed executive’s first-year total compensation, billed in three installments (engagement, milestone, completion). We are transparent on pricing from the first conversation and structure each engagement around outcomes, not hours.'
          }, {
            q: 'Do you work with PE and VC-backed organizations?',
            a: 'Yes — extensively. A significant portion of our practice supports private equity and venture capital portfolio companies, including investor-driven CEO transitions, value-creation hires, and pre-exit leadership team builds.'
          }, {
            q: 'How is your firm different from large global search firms?',
            a: 'Two differences matter most: every mandate is led personally by a senior consultant (not delegated to a research associate), and we measure ourselves on two-year retention rather than speed of placement. The result is a more discerning shortlist and significantly better long-term outcomes.'
          }].map((faq, i) => <details key={i} className="bg-white group" data-id="element-699">
                <summary className="flex items-start justify-between gap-6 p-8 cursor-pointer list-none" data-id="element-700">
                  <h3 className="text-lg md:text-xl font-serif font-bold text-navy-900 leading-tight flex-1" data-id="element-701">
                    {faq.q}
                  </h3>
                  <div className="w-10 h-10 rounded-full bg-gold-500/10 flex items-center justify-center flex-shrink-0 group-open:bg-gold-500 transition-colors" data-id="element-702">
                    <HelpCircle className="h-5 w-5 text-gold-600 group-open:text-white transition-colors" data-id="element-703" />
                  </div>
                </summary>
                <div className="px-8 pb-8 pt-0" data-id="element-704">
                  <p className="text-gray-600 leading-relaxed text-lg max-w-3xl" data-id="element-705">
                    {faq.a}
                  </p>
                </div>
              </details>)}
          </div>
        </div>
      </section>

      {/* ─── CTA ──────────────────────────────────────────────── */}
      <section className="py-28 bg-white" data-id="element-706">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 text-center" data-id="element-707">
          <div className="flex items-center justify-center gap-3 mb-6" data-id="element-708">
            <div className="w-12 h-px bg-gold-500" data-id="element-709" />
            <span className="text-gold-600 font-semibold tracking-[0.25em] uppercase text-xs" data-id="element-710">
              Begin a Conversation
            </span>
            <div className="w-12 h-px bg-gold-500" data-id="element-711" />
          </div>
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-navy-900 leading-tight mb-6" data-id="element-712">
            Ready to begin a search?
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto mb-10" data-id="element-713">
            Our senior consultants are available for a confidential scoping
            conversation. We will respond within one business day.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-714">
            <Button variant="gold" size="lg" href="mailto:contact@talentbridge.blog" data-id="element-715">
              Request a Consultation
              <ArrowRight className="ml-2 h-4 w-4" data-id="element-716" />
            </Button>
            <Button variant="outline" size="lg" href="/about" data-id="element-717">
              Meet the Firm
            </Button>
          </div>
        </div>
      </section>
    </div>;
}